import axios from "axios";
import {
  createCookie,
  deleteCookie,
  getPayload,
} from "../Components/authorization";
import { toast } from "react-toastify";

/* ---------------------------- ENV ---------------------------- */

const SSO_URL = process.env.REACT_APP_BASE_URL_SSO;
const APP_BASE_URL = process.env.REACT_APP_BASE_URL;
export const BASE_URL_ADMIN = process.env.REACT_APP_BASE_URL_ADMIN;

/* ---------------------------- AXIOS INSTANCES ---------------------------- */

const api = axios.create({
  baseURL: APP_BASE_URL,
});

const refreshAxios = axios.create({
  baseURL: SSO_URL,
});

/* ---------------------------- COOKIE READER ---------------------------- */

function readCookie(name) {
  const key = name + "=";
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(key)) return cookie.substring(key.length);
  }

  return null;
}

/* ---------------------------- LOGOUT ---------------------------- */

function logoutAndRedirect() {
  const redirectUrl = encodeURIComponent(
    window.location.pathname + window.location.search
  );

  deleteCookie("access");
  deleteCookie("refresh");

  localStorage.removeItem("notifications");

  window.location.href = `/login/?redirect=${redirectUrl}`;
}

/* ---------------------------- REFRESH TOKEN ---------------------------- */

async function refreshTokenRequest(refreshToken) {
  return refreshAxios.post("api/token/refresh/", {
    refresh: refreshToken,
  });
}

/* ---------------------------- REFRESH STATE ---------------------------- */

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });

  failedQueue = [];
};

/* ---------------------------- REQUEST INTERCEPTOR ---------------------------- */

api.interceptors.request.use(
  (config) => {
    const accessToken = readCookie("access");

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ---------------------------- RESPONSE INTERCEPTOR ---------------------------- */

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;
    const refreshToken = readCookie("refresh");

    const status = error?.response?.status;
    const errorCode = error?.response?.data?.code;

    const tokenExpiredErrors = [
      "malformed_access_token",
      "token_expired",
      "no_access",
      "token_not_valid",
    ];

    /* ---------------- NO REFRESH TOKEN ---------------- */

    if (!refreshToken) {
      logoutAndRedirect();
      return Promise.reject(error);
    }

    /* ---------------- PERMISSION ERROR ---------------- */

    if (
      status === 403 ||
      error?.response?.data?.detail ===
        "You do not have permission to perform this action."
    ) {
      toast.error("You don't have permission");
      return Promise.reject(error);
    }

    /* ---------------- TOKEN EXPIRED ---------------- */

    if (status === 401 || tokenExpiredErrors.includes(errorCode)) {
      if (originalRequest._retry) {
        logoutAndRedirect();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      /* -------- CHECK REFRESH TOKEN EXPIRY -------- */

      try {
        const payload = getPayload(refreshToken);

        if (Date.now() >= payload.exp * 1000) {
          logoutAndRedirect();
          return Promise.reject(error);
        }
      } catch {
        logoutAndRedirect();
        return Promise.reject(error);
      }

      /* -------- HANDLE MULTIPLE REFRESH CALLS -------- */

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = "Bearer " + token;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const res = await refreshTokenRequest(refreshToken);

        const newAccessToken = res.data.access;

        createCookie("access", newAccessToken);

        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        logoutAndRedirect();

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/* ---------------------------- API HELPERS ---------------------------- */

export default api;

export const fget = async ({ url, signal }) => {
  return api.get(url, { signal });
};

export const fget2 = async ({ url }) => {
  return api.get(url);
};

export const fgetNextPage = async ({ url }) => {
  return api.get(url);
};

export const fpatch = async ({ url, data }) => {
  return api.patch(url, data);
};

export const fpost = async ({ url, data }) => {
  return api.post(url, data);
};

export const fpost2 = async ({ url, data }) => {
  return axios.post(url, data);
};

export const fpostBFF = async ({ url, data }) => {
  // let headers = {
  //   Authorization: `Bearer ${token_value}`,
  // };
  const res = await api.post(
    process.env.REACT_APP_BASE_URL_MCBFF + `${url}`,
    data
  );
  return res;
};

export const fgetBFF = async ({ url }) => {
  return api.get(process.env.REACT_APP_BASE_URL_MCBFF + url);
};

export const fdelete = async ({ url }) => {
  return api.delete(url);
};

export const fdelete2 = async ({ url, data }) => {
  return api({
    method: "DELETE",
    url,
    data,
  });
};

/* ---------------------------- FILE EXPORT ---------------------------- */

export const exportFileWithXhr = ({
  url,
  data,
  updateProgressBar,
  transferComplete,
  transferFailed,
  transferCanceled,
}) => {
  return new Promise(function (resolve, reject) {
    var oReq = new XMLHttpRequest();

    oReq.upload.onprogress = updateProgressBar;
    oReq.addEventListener("load", transferComplete);
    oReq.addEventListener("error", transferFailed);
    oReq.addEventListener("abort", transferCanceled);

    oReq.open("PUT", url);
    oReq.setRequestHeader("Content-Type", "application/x-www-form-urlencoded");

    oReq.send(data);

    oReq.onreadystatechange = function () {
      if (oReq.status === 200) resolve();
    };
  });
};

/* ---------------------------- FILE UPLOAD ---------------------------- */

export const uploadFile = ({ url, data }) => {
  const fetchConfig = {
    method: "PUT",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: data,
  };

  return fetch(url, fetchConfig);
};
